import { useSuspenseQueries } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toRegularMetricResultOrError } from '../../../api/utils';
import { Languages, UNDEFINED_DISPLAY_KEY } from '../../../constants';
import {
  useGlobalLocaleContext,
  useLatestDomainPreferencesContext,
  useMetricDetailsMapContext,
  useMetricServiceContext,
} from '../../../context/contexts';
import { TimeSelection } from '../../../types';
import { sortFieldValues } from '../../../utils';
import { formatMetricValue } from '../../../utils-display';
import { ManualDataRefetchCountHandle, MetricTypes } from '../../types';
import { getFilterConditions } from '../../utils';
import {
  toNonSegmentedTimePeriodChartInput,
  toSingleLevelSegmentedTimePeriodChartInput,
} from '../charts/mui-charts/utils';
import {
  timeSliderStateToTimePeriodTimeSelection,
  toPartialNonSegmentedChartDataTimePeriod,
  toPartialSingleLevelSegmentedChartDataTimePeriod,
} from '../charts/timeperiod/utils';
import { getDefaultKpiBenchmarkTitle } from '../dashboards/utils';
import { Filter } from '../filter/filterbar/types';
import { ItemsState } from '../filter/types';
import { formatHierarchicalLabel } from '../filter/utils-display';
import { TimeSliderState } from '../timeslider/types';
import { KpiUiLayout, KpiUiSingleColumnOptions, KpiUiTwoColumnOptions } from './KpiUi';
import {
  KpiNormalQueryResponseValue,
  KpiQueryMetaData,
  KpiQueryResponseValue,
  KpiQueryValueInput,
  KpiSectionValue,
  KpiSectionValueInput,
  KpiSegmentedQueryResponseValue,
  KpiUiSubValue,
  KpiV2SingleColumnConfig,
  KpiV2TwoColumnConfig,
  ValueWithQueryMetaData,
} from './types';
import { getBenchmarkQueries, getFormattedKpiBenchmarkValue } from './utils';
// maybe this can be a generic useQueryValue function
const useKpiQueryValues = (kpiQueryValues: KpiQueryValueInput[]): KpiQueryResponseValue[] => {
  const { t } = useTranslation();
  const metricDetailsMap = useMetricDetailsMapContext();
  const metricService = useMetricServiceContext();
  const domainPreferencesRes = useLatestDomainPreferencesContext();
  const domainPreferences = domainPreferencesRes.settings;
  const queries = kpiQueryValues.map(({ query, manualDataRefetchCountHandle, timeSliderState, filtersState }) => {
    const { metrics, timeSelection, filters, segmentations } = query;
    const finalTimeSelection: TimeSelection = timeSliderState
      ? timeSliderStateToTimePeriodTimeSelection(timeSliderState)
      : timeSelection;
    const allFilters =
      [getFilterConditions(filtersState.items, MetricTypes.REGULAR, false), filters]
        .flatMap((f) => (f ? [f] : []))
        .join(' AND ') || undefined;
    const queryKey = JSON.stringify([
      'kpi-v2',
      metrics,
      finalTimeSelection,
      allFilters,
      segmentations,
      manualDataRefetchCountHandle.current,
    ]);
    return {
      queryKey: [queryKey],
      queryFn: () =>
        toRegularMetricResultOrError(
          metricService.queryRegularMetrics,
          metrics,
          finalTimeSelection,
          allFilters,
          segmentations
        ),
    };
  });
  const queryResults = useSuspenseQueries({
    queries,
  });

  const allData = queryResults.map(({ data }) => data);
  const formattedValues: KpiQueryResponseValue[] = allData.map((data, index) => {
    const { segmentations } = kpiQueryValues[index].query;
    let formattedValue: KpiQueryResponseValue;
    if (segmentations && segmentations.length > 0) {
      const chartInput = toSingleLevelSegmentedTimePeriodChartInput(
        data ?? [],
        toPartialSingleLevelSegmentedChartDataTimePeriod
      );
      if (!chartInput.series[0]) {
        return {
          responseType: 'segmented',
          segmentations: {
            value: [],
            queryMetaData: null,
          },
          valueFormatter: () => {
            // noop
            throw new Error('This should never be called if value is null');
          },
        };
      }
      const metricIdType = chartInput.series[0].metricId;
      const metricId = metricIdType.value;
      const metricDetails = metricDetailsMap[metricId];
      const valueFormatter = (value: number) =>
        formatMetricValue(value, metricDetails.formatType) ?? t(UNDEFINED_DISPLAY_KEY);
      // I think there will only be one series in the chartInput
      // TODO: Might want to add a validation step
      const { meta, dataField, values } = chartInput.series[0];
      const metricSql = meta?.metricSql;
      const segmentations = values.map((v, i) => {
        const d = chartInput.xAxisData[i];
        const label = formatHierarchicalLabel(d.value, dataField, t(UNDEFINED_DISPLAY_KEY), t);
        return {
          label,
          value: v as number,
        };
      });
      const sortedSegmentations = sortFieldValues(segmentations, dataField, domainPreferences);
      formattedValue = {
        responseType: 'segmented',
        segmentations: {
          value: sortedSegmentations,
          queryMetaData: {
            metric: metricIdType,
            metricsSql: metricSql ?? '',
          },
        },
        valueFormatter,
      };
    } else {
      const chartInput = toNonSegmentedTimePeriodChartInput(data ?? [], toPartialNonSegmentedChartDataTimePeriod);
      const series = chartInput.series;
      if (!series) {
        return {
          responseType: 'normal',
          // interpreting this as 0
          // I think the backend should ideally separate between null and 0
          valueWithQueryMetaData: {
            value: 0,
            queryMetaData: null,
          },
          valueFormatter: () => {
            // noop
            throw new Error('This should never be called if value is null');
          },
        };
      }
      const { meta } = series;
      const metricIdType = series.metricId;
      const metricId = metricIdType.value;
      const metricSql = meta?.metricSql;
      const metricDetails = metricDetailsMap[metricId];
      const valueFormatter = (value: number) =>
        formatMetricValue(value, metricDetails.formatType) ?? t(UNDEFINED_DISPLAY_KEY);
      return {
        responseType: 'normal',
        valueWithQueryMetaData: {
          value: series.values[0] as number,
          queryMetaData: {
            metric: metricIdType,
            metricsSql: metricSql ?? '',
          },
        },
        valueFormatter,
      };
    }
    return formattedValue;
  });
  return formattedValues;
};

const useMainQueryValue = (kpiSectionValueInput: KpiSectionValueInput): KpiNormalQueryResponseValue => {
  const { query, manualDataRefetchCountHandle, timeSliderState, filtersState } = kpiSectionValueInput;
  const { segmentations, ...queryWithoutSegmentations } = query;
  const mainQueryValue = useKpiQueryValues([
    {
      query: queryWithoutSegmentations,
      manualDataRefetchCountHandle,
      timeSliderState,
      filtersState,
    },
  ])[0] as KpiNormalQueryResponseValue;
  return mainQueryValue;
};

const useBenchmarkQueryValues = (kpiSectionValueInput: KpiSectionValueInput): KpiNormalQueryResponseValue[] => {
  const { query, manualDataRefetchCountHandle, timeSliderState, filtersState, benchmarkConfigs } = kpiSectionValueInput;
  // Benchmark on a query with segmentation will give benchmark values for the
  // unsegmented query.
  const { segmentations, ...queryWithoutSegmentations } = query;
  const hasBenchmarkValues = benchmarkConfigs && benchmarkConfigs.length > 0;
  const benchmarkQueries = hasBenchmarkValues ? getBenchmarkQueries(queryWithoutSegmentations, benchmarkConfigs) : [];
  const benchmarkQueryValues = useKpiQueryValues(
    benchmarkQueries.map((query) => ({
      query,
      manualDataRefetchCountHandle,
      timeSliderState,
      filtersState,
    }))
  ) as KpiNormalQueryResponseValue[];
  return benchmarkQueryValues;
};

const useSegmentationQueryValues = (kpiSectionValueInput: KpiSectionValueInput): KpiSegmentedQueryResponseValue[] => {
  const { query, manualDataRefetchCountHandle, timeSliderState, filtersState } = kpiSectionValueInput;
  const hasSegmentations = query.segmentations && query.segmentations.length > 0;
  const segmentationQueryValues = useKpiQueryValues(
    hasSegmentations
      ? [
          {
            query,
            manualDataRefetchCountHandle,
            timeSliderState,
            filtersState,
          },
        ]
      : []
  ) as KpiSegmentedQueryResponseValue[];
  return segmentationQueryValues;
};

const useSubQueryValues = (kpiSectionValueInput: KpiSectionValueInput): KpiNormalQueryResponseValue[] => {
  const { manualDataRefetchCountHandle, timeSliderState, filtersState, subQueries } = kpiSectionValueInput;
  const subQueryInputs =
    subQueries?.map((subQuery) => ({
      query: subQuery.query,
      manualDataRefetchCountHandle,
      timeSliderState,
      filtersState,
    })) ?? [];
  const subQueryValues = useKpiQueryValues(subQueryInputs) as KpiNormalQueryResponseValue[];
  return subQueryValues;
};

const useMainSectionValue = (
  mainQueryValue: KpiNormalQueryResponseValue,
  kpiSectionValueInput: KpiSectionValueInput
): ValueWithQueryMetaData<string> | null => {
  const { query, segmentationConfig } = kpiSectionValueInput;
  const hasSegmentations = query.segmentations && query.segmentations.length > 0;
  const hasMainValue = !hasSegmentations || (segmentationConfig?.showMainValue ?? true);
  const { valueFormatter, valueWithQueryMetaData } = mainQueryValue;
  const { value, queryMetaData } = valueWithQueryMetaData;
  return hasMainValue ? { value: valueFormatter(value), queryMetaData } : null;
};

const useBenchmarkSectionValues = (
  benchmarkQueryValues: KpiNormalQueryResponseValue[],
  mainQueryValue: KpiNormalQueryResponseValue,
  kpiSectionValueInput: KpiSectionValueInput
): ValueWithQueryMetaData<KpiUiSubValue>[] => {
  const { t } = useTranslation();
  const { benchmarkConfigs } = kpiSectionValueInput;
  let benchmarkValues: ValueWithQueryMetaData<KpiUiSubValue>[] = benchmarkQueryValues.map(
    (benchmarkQueryValue, index) => {
      const { benchmarkType, benchmarkPeriod } = benchmarkConfigs[index];
      const { valueWithQueryMetaData, valueFormatter } = benchmarkQueryValue;
      const { value, queryMetaData } = valueWithQueryMetaData;
      const formattedBenchmarkValue = getFormattedKpiBenchmarkValue(
        benchmarkType,
        value,
        mainQueryValue.valueWithQueryMetaData.value,
        valueFormatter,
        t(UNDEFINED_DISPLAY_KEY)
      );

      return {
        value: {
          title: getDefaultKpiBenchmarkTitle(benchmarkPeriod, benchmarkType, t),
          value: formattedBenchmarkValue,
        },
        queryMetaData,
      };
    }
  );
  return benchmarkValues;
};

const useSegmentationSectionValues = (
  segmentationQueryValues: KpiSegmentedQueryResponseValue[]
): ValueWithQueryMetaData<KpiUiSubValue>[] => {
  if (segmentationQueryValues.length === 0) {
    return [];
  }
  const { segmentations, valueFormatter } = segmentationQueryValues[0] as KpiSegmentedQueryResponseValue;
  const { value, queryMetaData } = segmentations;
  return value.map(({ label, value }) => {
    return {
      value: {
        title: label,
        value: valueFormatter(value),
      },
      queryMetaData,
    };
  });
};

const useSubQuerySectionValues = (
  subQueryValues: KpiNormalQueryResponseValue[],
  kpiSectionValueInput: KpiSectionValueInput
): ValueWithQueryMetaData<KpiUiSubValue>[] => {
  const { selected } = useGlobalLocaleContext();
  if (subQueryValues.length === 0) {
    return [];
  }
  const { subQueries } = kpiSectionValueInput;
  return subQueries.map((subQuery, index) => {
    const { valueWithQueryMetaData, valueFormatter } = subQueryValues[index];
    const { value, queryMetaData } = valueWithQueryMetaData;
    return {
      value: {
        title: subQuery.title?.[selected] ?? '',
        value: valueFormatter(value),
      },
      queryMetaData,
    };
  });
};

export const useKpiSectionValue = (kpiSectionValueInput: KpiSectionValueInput): KpiSectionValue => {
  const mainQueryValue = useMainQueryValue(kpiSectionValueInput);
  const benchmarkQueryValues = useBenchmarkQueryValues(kpiSectionValueInput);
  const segmentationQueryValues = useSegmentationQueryValues(kpiSectionValueInput);
  const subQueryValues = useSubQueryValues(kpiSectionValueInput);
  const mainValue = useMainSectionValue(mainQueryValue, kpiSectionValueInput);
  const benchmarkValues = useBenchmarkSectionValues(benchmarkQueryValues, mainQueryValue, kpiSectionValueInput);
  const segmentedValues = useSegmentationSectionValues(segmentationQueryValues);
  const subValues = useSubQuerySectionValues(subQueryValues, kpiSectionValueInput);
  return {
    mainValue,
    benchmarkValues,
    segmentedValues,
    subValues,
  };
};

const getMainValueAndSubValuesFromSectionValues = (
  values: KpiSectionValue
): { mainValue?: ValueWithQueryMetaData<string>; subValues: ValueWithQueryMetaData<KpiUiSubValue>[] } => {
  const mainValue = values.mainValue ?? undefined;
  const subValues = [...values.segmentedValues, ...values.benchmarkValues, ...values.subValues];
  return {
    mainValue,
    subValues,
  };
};

export const useKpiV2SingleColumnValues = (
  config: KpiV2SingleColumnConfig,
  manualDataRefetchCountHandle: ManualDataRefetchCountHandle,
  timeSliderState: TimeSliderState | null,
  filtersState: ItemsState<Filter>,
  selectedLocale: Languages
): { meta: KpiQueryMetaData[]; uiOptions: KpiUiSingleColumnOptions } => {
  const { title, subtitle, section, footer } = config;
  const values = useKpiSectionValue({
    query: section.query,
    benchmarkConfigs: section.benchmarkConfigs ?? [],
    segmentationConfig: section.segmentationConfig ?? null,
    subQueries: section.subQueries ?? [],
    manualDataRefetchCountHandle,
    timeSliderState,
    filtersState,
  });
  const kpiUiSection = getMainValueAndSubValuesFromSectionValues(values);
  const kpiUiSectionValue = {
    mainValue: kpiUiSection.mainValue?.value,
    subValues: kpiUiSection.subValues.map((v) => v.value),
  };

  const { mainValue, subValues } = kpiUiSection;
  const meta = [mainValue, ...subValues].flatMap((v) => v?.queryMetaData ?? []);

  return {
    meta,
    uiOptions: {
      layout: KpiUiLayout.SINGLE_COLUMN,
      title: title?.[selectedLocale],
      subtitle: subtitle?.[selectedLocale],
      section: kpiUiSectionValue,
      footer: footer?.[selectedLocale],
    },
  };
};

export const useKpiV2TwoColumnValues = (
  config: KpiV2TwoColumnConfig,
  manualDataRefetchCountHandle: ManualDataRefetchCountHandle,
  timeSliderState: TimeSliderState | null,
  filtersState: ItemsState<Filter>,
  selectedLocale: Languages
): { meta: KpiQueryMetaData[]; uiOptions: KpiUiTwoColumnOptions } => {
  const { title, subtitle, sections, footer } = config;
  const leftSectionValues = useKpiSectionValue({
    query: sections.leftSection.query,
    benchmarkConfigs: sections.leftSection.benchmarkConfigs ?? [],
    segmentationConfig: sections.leftSection.segmentationConfig ?? null,
    subQueries: sections.leftSection.subQueries ?? [],
    manualDataRefetchCountHandle,
    timeSliderState,
    filtersState,
  });
  const rightSectionValues = useKpiSectionValue({
    query: sections.rightSection.query,
    benchmarkConfigs: sections.rightSection.benchmarkConfigs ?? [],
    segmentationConfig: sections.rightSection.segmentationConfig ?? null,
    subQueries: sections.rightSection.subQueries ?? [],
    manualDataRefetchCountHandle,
    timeSliderState,
    filtersState,
  });
  const leftValue = getMainValueAndSubValuesFromSectionValues(leftSectionValues);
  const rightValue = getMainValueAndSubValuesFromSectionValues(rightSectionValues);
  const { mainValue: leftMainValue, subValues: leftSubValues } = leftValue;
  const { mainValue: rightMainValue, subValues: rightSubValues } = rightValue;
  const meta = [leftMainValue, ...leftSubValues, rightMainValue, ...rightSubValues].flatMap(
    (v) => v?.queryMetaData ?? []
  );
  const leftSection = {
    mainValue: leftMainValue?.value,
    subValues: leftSubValues.map((v) => v.value),
    title: sections.leftSection.title?.[selectedLocale],
  };
  const rightSection = {
    mainValue: rightMainValue?.value,
    subValues: rightSubValues.map((v) => v.value),
    title: sections.rightSection.title?.[selectedLocale],
  };
  return {
    meta,
    uiOptions: {
      layout: KpiUiLayout.TWO_COLUMN,
      title: title?.[selectedLocale],
      subtitle: subtitle?.[selectedLocale],
      sections: {
        leftSection,
        rightSection,
      },
      footer: footer?.[selectedLocale],
    },
  };
};
